/**
 * Phase 2.4 — Helper to pick a localized string from a service document.
 *
 * Services returned by /api/services may carry an optional `{field}_translations`
 * sub-document ({ en: "...", fr: "..." }). If present and the entry for the
 * current language is non-empty, use it. Otherwise fall back to the original
 * field (always stored at the root of the document).
 *
 * This helper is defensive by design: if the service object is missing, if the
 * field is missing, or if the translations sub-document is malformed, it
 * returns an empty string instead of throwing.
 *
 * Example:
 *   const title = pickTranslation(service, 'title', i18n.language);
 *   const description = pickTranslation(service, 'description', i18n.language);
 */
// Normalise les locales composées (ex: "en-US" -> "en", "fr-FR" -> "fr").
// Évite les cas où le navigateur renvoie "en-US" alors que les traductions
// sont stockées sous la clé courte "en".
const normalizeLang = (lang) => {
  if (!lang || typeof lang !== 'string') return '';
  return lang.toLowerCase().split(/[-_]/)[0];
};

export function pickTranslation(service, field, lang) {
  if (!service || !field) return '';
  const original = service[field];
  const short = normalizeLang(lang);
  if (!short) return original || '';
  const map = service[`${field}_translations`];
  if (map && typeof map === 'object') {
    // 1) match exact (ex: "en"), 2) match court après normalisation côté map.
    const localized = map[short] || map[lang];
    if (typeof localized === 'string' && localized.trim().length > 0) {
      return localized;
    }
  }
  return original || '';
}

/**
 * Shortcut for the two main listing fields used across the marketplace.
 * Returns { title, description } already localized with fallbacks applied.
 */
export function localizeService(service, lang) {
  return {
    title: pickTranslation(service, 'title', lang),
    description: pickTranslation(service, 'description', lang),
    conditions: pickTranslation(service, 'conditions', lang),
  };
}
